import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from './useAuth';
import { Wrench, Search, Truck, MoreHorizontal } from 'lucide-react';

// Icon mapping for appointment types
const TYPE_ICONS = {
  approved_work: Wrench,
  inspection: Search,
  supplier_visit: Truck,
  other: MoreHorizontal,
};

// Default appointment types (fallback)
const DEFAULT_TYPES = [
  { value: 'approved_work', label: 'Approved Work', color: '#22c55e', bgColor: '#dcfce7' },
  { value: 'inspection', label: 'Inspection', color: '#3b82f6', bgColor: '#dbeafe' },
  { value: 'supplier_visit', label: 'Supplier Visit', color: '#f59e0b', bgColor: '#fef3c7' },
  { value: 'other', label: 'Other', color: '#64748b', bgColor: '#f1f5f9' },
];

const AppointmentTypesContext = createContext(null);

export function AppointmentTypesProvider({ children }) {
  const [types, setTypes] = useState(DEFAULT_TYPES);
  const [loading, setLoading] = useState(true);

  // Fetch appointment types from backend
  const fetchTypes = useCallback(async () => {
    try {
      const response = await api.get('/settings/appointment-types');
      if (response.data?.types) {
        setTypes(response.data.types);
      }
    } catch (error) {
      console.log('Using default appointment types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  // Update appointment types
  const updateTypes = async (newTypes) => {
    const response = await api.put('/settings/appointment-types', newTypes);
    if (response.data?.types) {
      setTypes(response.data.types);
    }
    return response.data;
  };

  // Get type info with icon
  const getTypeInfo = useCallback((typeValue) => {
    const type = types.find(t => t.value === typeValue) || types.find(t => t.value === 'other') || DEFAULT_TYPES[3];
    return {
      ...type,
      icon: TYPE_ICONS[type.value] || MoreHorizontal,
    };
  }, [types]);

  // Get types as a map for quick lookup
  const typesMap = types.reduce((acc, type) => {
    acc[type.value] = {
      ...type,
      icon: TYPE_ICONS[type.value] || MoreHorizontal,
    };
    return acc;
  }, {});

  return (
    <AppointmentTypesContext.Provider value={{ 
      types, 
      typesMap,
      loading, 
      updateTypes, 
      refreshTypes: fetchTypes,
      getTypeInfo,
      TYPE_ICONS,
    }}>
      {children}
    </AppointmentTypesContext.Provider>
  );
}

export function useAppointmentTypes() {
  const context = useContext(AppointmentTypesContext);
  if (!context) {
    throw new Error('useAppointmentTypes must be used within an AppointmentTypesProvider');
  }
  return context;
}
